import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { APP_THEME } from '../constants/local_storage_constants';

const AppThemeContext = createContext(null);
export const useAppTheme = () => useContext(AppThemeContext);

const readThemePreference = () => {
  try {
    const saved = localStorage.getItem(APP_THEME);
    if (saved === 'light' || saved === 'system') return saved;
  } catch (_) {
    // Appearance remains available when browser storage is blocked.
  }
  return 'dark';
};

export const createAppTheme = (mode) => {
  const dark = mode === 'dark';
  return createTheme({
    palette: {
      mode,
      // Keep these semantic colors aligned with theme.css. MUI's color
      // utilities need concrete colors rather than CSS custom properties.
      primary: { main: dark ? '#8b9aff' : '#5468ff' },
      secondary: { main: '#11cb5f' },
      background: { default: dark ? '#000000' : '#ffffff', paper: dark ? '#1c1c1e' : '#ffffff' },
      text: { primary: dark ? '#f5f5f7' : '#202124', secondary: dark ? '#a1a1a6' : '#69696e' },
      divider: dark ? 'rgba(84, 84, 88, .65)' : 'rgba(60, 60, 67, .16)',
      error: { main: dark ? '#ff7474' : '#b42318' },
    },
    components: {
      MuiModal: {
        defaultProps: {
          // Keep modal/ARIA ownership stable while resizing the desktop frame.
          container: () => document.getElementById('appViewport') || document.body,
          disableScrollLock: true,
        },
      },
      MuiPaper: {
        // iOS raised surfaces use a solid semantic fill, including drawers.
        styleOverrides: { root: { backgroundImage: 'none' } },
      },
    },
  });
};

export default function AppThemeProvider({ children }) {
  const [preference, setPreferenceState] = useState(readThemePreference);
  const systemDark = useMediaQuery('(prefers-color-scheme: dark)', { noSsr: true });
  const mode = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;
  const theme = useMemo(() => createAppTheme(mode), [mode]);
  const setPreference = useCallback(value => {
    setPreferenceState(value);
    try {
      localStorage.setItem(APP_THEME, value);
    } catch (_) {
      // Apply the selection for this session even when it cannot be saved.
    }
  }, []);
  const settings = useMemo(() => ({ preference, setPreference }), [preference, setPreference]);

  useEffect(() => {
    const syncPreference = event => {
      if (event.key === APP_THEME || event.key === null) setPreferenceState(readThemePreference());
    };
    window.addEventListener('storage', syncPreference);
    return () => window.removeEventListener('storage', syncPreference);
  }, []);

  useLayoutEffect(() => {
    // Also reaches body-level portals and updates before the next paint.
    document.documentElement.dataset.theme = mode;
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) themeColor.content = mode === 'dark' ? '#000000' : '#ffffff';
  }, [mode]);

  return <AppThemeContext.Provider value={settings}>
    <ThemeProvider theme={theme}>{children}</ThemeProvider>
  </AppThemeContext.Provider>;
}
